import { PaginationProps, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AxiosError } from "axios";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ConfirmPopup from "../../commons/components/ConfirmPopup/ConfirmPopup";
import CustomPagination from "../../commons/components/CustomPagination";
import CustomTable from "../../commons/components/CustomTable";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";
import {
  MERCHANT_SETTLEMENT_DETAIL_INVOLVE_STORE_URL,
  MERCHANT_SETTLEMENT_DETAIL_ORDER_EXPORT_URL,
  MERCHANT_SETTLEMENT_DETAIL_ORDER_URL,
  MERCHANT_SETTLEMENT_DETAIL_URL,
  MERCHANT_SETTLEMENT_REQUEST_PAYMENT_CONFIRM_URL,
  MERCHANT_SETTLEMENT_REQUEST_PAYMENT_URL,
} from "../../commons/constants/api-urls";
import useFetch from "../../commons/hooks/useFetch";
import useFetchList from "../../commons/hooks/useFetchList";
import useToast from "../../commons/hooks/useToast";
import defaultAxios from "../../commons/utils/axios";
import { format2Digit } from "../../commons/utils/functions/formatPrice";
import { formatDateTime } from "../../commons/utils/functions/helper";
import { AppContext } from "../../contexts/AppContext";
import { SettlementStatus } from "./index";
import styles from "./reconciliation.module.scss";

interface DataType {
  DELIVERY_FEE: number;
  DISCOUNT_AMOUNT: number;
  FIAT_AMOUNT: number;
  ITEM_AMOUNT: number;
  LP_AMOUNT: number;
  LP_DISCOUNT_AMOUNT: number;
  LP_MERCHANT_EARNED: number;
  LP_SPENT: number;
  NET_AMOUNT: number;
  PLATFORM_FEE: number;
  TOTAL_ORDER: number;
  TOTAL_USER_PAID_AMOUNT: number;
  VOUCHER_DISCOUNT_AMOUNT: number;
}

interface DataTypeOrder {
  key: React.Key;
  no: number;
  orderId: number;
  date: string;
  compDate: string;
  storeId: string;
  store: string;
  platformFee: number;
  shippingFee: number;
  lpEarned: number;
  profit: number;
  status: number;
}

const ReconciliationDetail: React.FC = () => {
  const { user } = useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const toast = useToast();
  const [confirmPayment, setConfirmPayment] = useState(false);
  const [confirmPaymentReceived, setConfirmPaymentReceived] = useState(false);

  const { data: storeList } = useFetch<{ store_id: string; store_name: string }[]>(
    MERCHANT_SETTLEMENT_DETAIL_INVOLVE_STORE_URL(id ? id : "")
  );

  const storeOptions = useMemo(() => {
    const options = [{ value: "", label: "All store" }];
    if (storeList) {
      const stores = storeList.map(store => ({
        value: store.store_id.toString(),
        label: store.store_name,
      }));
      for (const store of stores) {
        options.push(store);
      }
    }
    return options;
  }, [storeList]);

  const [storeId, setStoreId] = useState<string | "">("");

  const handleChangeStore = (value: string) => {
    setStoreId(value);
    setPage(1);
  };

  const { data: settlementDetail, refresh } = useFetch<SettlementItem>(MERCHANT_SETTLEMENT_DETAIL_URL(id ? id : -1));

  const param = {
    settlement_id: id,
    page: page,
    perPage: perPage,
    paginationMetadataStyle: "body",
    merchant_store_id: storeId ? storeId : "",
  };
  const { data: orders, total } = useFetchList<SettlementOrder>(
    MERCHANT_SETTLEMENT_DETAIL_ORDER_URL(user?.merchantIds ? user.merchantIds[0] : -1),
    param
  );

  const data: DataType[] = useMemo(() => {
    if (settlementDetail) {
      return [{ ...settlementDetail.amount_breakdown, status: settlementDetail.status }];
    }
    return [];
  }, [settlementDetail]);

  const dataOrder: any[] = useMemo(() => {
    return orders.map((product: SettlementOrder, index) => {
      return {
        key: product.id.toString(),
        no: index + 1,
        settlementId: moment(product.settlement.last_transaction_date).format("DD/MM/YYYY") + "-" + product?.store?.id,
        last_transaction_date: product.settlement.last_transaction_date,
        status: product.merchantSettlementStatus,
        orderId: product.id,
        mode: product.shipment_method,
        subtotal: product.payment.amount,
        markUpAmount: product.payment.markUpAmount,
        loyalty_point: product.payment.rewardLoyaltyPointForMerchant * product.payment.loyaltyPointExchangeRate,
        receivableFiatAmount: product.payment.receivableFiatAmount,
      };
    });
  }, [orders]);

  const columns: ColumnsType<DataType> = [
    {
      title: "Total Sales",
      dataIndex: "ITEM_AMOUNT",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
    {
      title: "Total Order Count",
      dataIndex: "TOTAL_ORDER",
      render: (text, record) => {
        return <div>{total}</div>;
      },
    },
    {
      title: "Total Platform Cost",
      dataIndex: "PLATFORM_FEE",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
    // {
    //   title: "Total LP Collected",
    //   dataIndex: "LP_MERCHANT_EARNED_AMOUNT",
    //   render: (text, record) => {
    //     return <div>S${format2Digit(text)}</div>;
    //   },
    // },
    {
      title: "Total Refund",
      dataIndex: "total_refund",
      render: (text, record) => {
        return <div>-S${format2Digit(0)}</div>;
      },
    },
    {
      title: "Total Settlement Amount",
      dataIndex: "FIAT_AMOUNT",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: text => {
        return (
          <div style={{ color: `${text !== SettlementStatus.PROCESSING ? " #12B76A" : " #f79009"}` }}>
            {text !== 3 ? "Processing" : "Paid"}
          </div>
        );
      },
    },
  ];

  const columnsOrders: ColumnsType<DataTypeOrder> = [
    {
      title: "No",
      dataIndex: "no",
      render: (_, record) => {
        return <div className={styles.productName}>{record.no}</div>;
      },
    },
    {
      title: "Settlement Report ID",
      dataIndex: "settlementId",
      key: "settlementId",
      render: (text: string) => <span>{text || "khong co"}</span>,
    },
    {
      title: "Transaction Date/Time",
      dataIndex: "last_transaction_date",
      key: "last_transaction_date",
      render: (text: string) => <span>{formatDateTime(text)}</span>,
    },

    {
      title: "Order ID",
      dataIndex: "orderId",
      render: (text, record) => {
        return <div>{text}</div>;
      },
    },
    {
      title: "Mode",
      dataIndex: "mode",
      render: (text, record) => {
        return <div>{text}</div>;
      },
    },

    {
      title: "Sales",
      dataIndex: "subtotal",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
    {
      title: "Platform Cost",
      dataIndex: "markUpAmount",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
    // {
    //   title: "LP Collected",
    //   dataIndex: "loyalty_point",
    //   render: (text, record) => {
    //     return <div>S${format2Digit(text)}</div>;
    //   },
    // },
    {
      title: "Refund",
      dataIndex: "refund",
      render: (text, record) => {
        return <div>-S${format2Digit(0)}</div>;
      },
    },
    {
      title: "Settlement Amount",
      dataIndex: "receivableFiatAmount",
      render: (text, record) => {
        return <div>S${format2Digit(text)}</div>;
      },
    },
  ];

  const clickExportCsv = async () => {
    const FileDownload = require("js-file-download");
    const url = `${MERCHANT_SETTLEMENT_DETAIL_ORDER_EXPORT_URL(
      user?.merchantIds ? user.merchantIds[0] : -1
    )}?settlement_id=${id}`;
    defaultAxios.get(url, { responseType: "blob" }).then(response => {
      FileDownload(
        response.data,
        `${moment(settlementDetail?.create_time).format("DD/MM/yyyy")}-settlement-report.csv`
      );
    });
  };

  const clickRequestFIATPayment = async () => {
    try {
      await defaultAxios.put(MERCHANT_SETTLEMENT_REQUEST_PAYMENT_URL, { settlementIds: [Number(id)] });
      toast.success("Request success");
      setConfirmPayment(false);
      refresh();
    } catch (err) {
      const error = (err as AxiosError<any>)?.response?.data;
      const message =
        typeof error?.error.message === "string"
          ? error?.error.message
          : typeof error?.error.message?.[0] === "string"
          ? error.error.message[0]
          : "Request Fail";
      toast.error(message);
      return "";
    }
  };

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setPerPage(pageSize);
    setPage(current);
  };

  const clickRequestFIATPaymentReceived = async () => {
    try {
      await defaultAxios.post(MERCHANT_SETTLEMENT_REQUEST_PAYMENT_CONFIRM_URL, { settlementIds: [Number(id)] });
      toast.success("Request success");
      setConfirmPaymentReceived(false);
      refresh();
    } catch (err) {
      const error = (err as AxiosError<any>)?.response?.data;
      const message =
        typeof error?.error.message === "string"
          ? error?.error.message
          : typeof error?.error.message?.[0] === "string"
          ? error.error.message[0]
          : "Request Fail";
      toast.error(message);
      return "";
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrapper}>
        <div className={styles.title}>{moment(settlementDetail?.create_time).format("DD/MM/yyyy")}</div>
      </div>

      <div className={styles.productPanel}>
        <div className={styles.tabContent}>
          <CustomTable pagination={false} columns={columns} dataSource={data} />
        </div>
      </div>

      <div className={styles.filterReconciliation}>
        <div className={styles.left}>
          <p>Fillter by Store </p>
          <Select
            defaultValue=""
            className="select-reconciliation"
            style={{ width: 250 }}
            onChange={handleChangeStore}
            options={storeOptions}
          />
        </div>
        <PrimaryButton className={styles.addProductBtn} onClick={() => clickExportCsv()}>
          Export CSV
        </PrimaryButton>
      </div>

      <div className={styles.productPanel}>
        <div className={styles.tabContent}>
          <CustomTable pagination={false} columns={columnsOrders} dataSource={dataOrder} scroll={{ x: true }} />
        </div>
      </div>
      {total > 10 && (
        <div className={styles.paginationWrapper}>
          <CustomPagination
            defaultCurrent={page}
            onChange={page => setPage(page)}
            showSizeChanger
            total={total}
            onShowSizeChange={onShowSizeChange}
          />
        </div>
      )}

      <ConfirmPopup
        open={confirmPayment}
        onCancel={() => setConfirmPayment(false)}
        title="Request Payment"
        description={`Are you sure you want to proceed with the request?`}
        onConfirm={clickRequestFIATPayment}
      />

      <ConfirmPopup
        open={confirmPaymentReceived}
        onCancel={() => setConfirmPaymentReceived(false)}
        title="Payment Received"
        description={`Are you sure you want to confirm?`}
        onConfirm={clickRequestFIATPaymentReceived}
      />
    </div>
  );
};

export default ReconciliationDetail;
